import React from 'react';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, ResponsiveContainer
} from 'recharts';

const DashboardModal = ({ isOpen, onClose, driverName, raceHistoryData = [] }) => {
  if (!isOpen) return null;

  const calculateStats = () => {
    if (!raceHistoryData || raceHistoryData.length === 0) {
      return {
        bestET: 'N/A',
        bestMPH: 'N/A',
        winRate: '0%',
        totalRaces: 0,
        avgET: 'N/A',
        bestRT: 'N/A',
        totalWins: 0,
        totalLosses: 0,
        best60: 'N/A',
        best330: 'N/A',
        best8ET: 'N/A',
        best8MPH: 'N/A',
        best4ET: 'N/A',
        best4MPH: 'N/A',
        avgRT: 'N/A',
        categories: {},
        consistency: 0,
        recentForm: 0
      };
    }

    const races = raceHistoryData;
    const totalRaces = races.length;
    const wins = races.filter(r => r.win).length;
    const losses = totalRaces - wins;
    const winRate = totalRaces > 0 ? Math.round((wins / totalRaces) * 100) : 0;

    const numericET = races
      .map(r => parseFloat(r.ft8))
      .filter(v => !isNaN(v));
    const bestET = numericET.length > 0 ? Math.min(...numericET).toFixed(3) : 'N/A';
    const avgET = numericET.length > 0 ? (numericET.reduce((a, b) => a + b, 0) / numericET.length).toFixed(3) : 'N/A';

    const numericMPH = races
      .map(r => parseFloat(r.ft8mph))
      .filter(v => !isNaN(v));
    const bestMPH = numericMPH.length > 0 ? Math.max(...numericMPH).toFixed(2) : 'N/A';

    const numericRT = races
      .map(r => parseFloat(r.rt))
      .filter(v => !isNaN(v));
    const bestRT = numericRT.length > 0 ? Math.min(...numericRT).toFixed(3) : 'N/A';
    const avgRT = numericRT.length > 0 ? (numericRT.reduce((a, b) => a + b, 0) / numericRT.length).toFixed(3) : 'N/A';

    const numeric60 = races.map(r => parseFloat(r.ft60)).filter(v => !isNaN(v));
    const best60 = numeric60.length > 0 ? Math.min(...numeric60).toFixed(3) : 'N/A';

    const numeric330 = races.map(r => parseFloat(r.ft330)).filter(v => !isNaN(v));
    const best330 = numeric330.length > 0 ? Math.min(...numeric330).toFixed(3) : 'N/A';

    const numeric4ET = races.map(r => parseFloat(r.ft4)).filter(v => !isNaN(v) && v > 0);
    const best4ET = numeric4ET.length > 0 ? Math.min(...numeric4ET).toFixed(3) : 'N/A';

    const numeric4MPH = races.map(r => parseFloat(r.ft4mph)).filter(v => !isNaN(v) && v > 0);
    const best4MPH = numeric4MPH.length > 0 ? Math.max(...numeric4MPH).toFixed(2) : 'N/A';

    const categories = {};
    races.forEach(race => {
      const cat = race.category || 'Unknown';
      if (!categories[cat]) categories[cat] = { wins: 0, losses: 0, total: 0 };
      categories[cat].total++;
      if (race.win) categories[cat].wins++;
      else categories[cat].losses++;
    });

    const recentRaces = races.slice(0, 3);
    const recentWins = recentRaces.filter(r => r.win).length;
    const recentForm = recentRaces.length > 0 ? Math.round((recentWins / recentRaces.length) * 100) : 0;

    const consistency = numericET.length > 1 ? Math.max(0, 100 - (Math.sqrt(numericET.reduce((sum, val, idx) => sum + Math.pow(val - (avgET === 'N/A' ? 0 : parseFloat(avgET)), 2), 0) / numericET.length) * 10)) : 0;

    return {
      bestET,
      bestMPH,
      winRate: `${winRate}%`,
      totalRaces,
      avgET,
      bestRT,
      totalWins: wins,
      totalLosses: losses,
      best60,
      best330,
      best4ET,
      best4MPH,
      avgRT,
      categories,
      consistency: Math.round(consistency),
      recentForm
    };
  };

  const prepareChartData = () => {
    if (!raceHistoryData || raceHistoryData.length === 0) {
      return { winLoss: [], categoryChart: [], etTrend: [] };
    }

    // Win/Loss Pie Chart Data
    const wins = raceHistoryData.filter(r => r.win).length;
    const losses = raceHistoryData.length - wins;
    const winLoss = [
      { name: 'Wins', value: wins, fill: '#27ae60' },
      { name: 'Losses', value: losses, fill: '#e74c3c' }
    ];

    // Category Performance Bar Chart Data
    const categoryChart = Object.entries(stats.categories).map(([name, data]) => ({
      category: name,
      winRate: Math.round((data.wins / data.total) * 100),
      wins: data.wins,
      losses: data.losses
    }));

    // ET Trend Line Chart Data (reverse to show chronological order)
    const etTrend = raceHistoryData
      .slice()
      .reverse()
      .map((race, idx) => ({
        race: `Race ${idx + 1}`,
        et: parseFloat(race.ft8) || 0,
        result: race.win ? 'W' : 'L'
      }));

    return { winLoss, categoryChart, etTrend };
  };

  const stats = calculateStats();
  const chartData = prepareChartData();

  return (
    <div className="modal-overlay-dashboard" onClick={onClose}>
      <div className="modal-dashboard-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} title="Close">✕</button>

        <div className="driver-dashboard-container-modal">
          {/* Header */}
          <div className="dashboard-header">
            <div className="driver-profile-info">
              <h2 className="driver-name">{driverName}</h2>
              <p className="driver-subtitle">Race Performance Profile</p>
            </div>
          </div>

          {/* Win/Loss Summary */}
          <div className="win-loss-summary">
            <div className="summary-card wins">
              <div className="summary-icon">🏆</div>
              <div className="summary-content">
                <div className="summary-label">Wins</div>
                <div className="summary-value">{stats.totalWins}</div>
              </div>
            </div>
            <div className="summary-card total">
              <div className="summary-icon">🎯</div>
              <div className="summary-content">
                <div className="summary-label">Total Races</div>
                <div className="summary-value">{stats.totalRaces}</div>
              </div>
            </div>
            <div className="summary-card losses">
              <div className="summary-icon">📉</div>
              <div className="summary-content">
                <div className="summary-label">Losses</div>
                <div className="summary-value">{stats.totalLosses}</div>
              </div>
            </div>
          </div>

          {/* Main Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">⚡</div>
              <div className="stat-content">
                <div className="stat-label">Best ET</div>
                <div className="stat-value">{stats.bestET}s</div>
                <div className="stat-subtext">1/8 Mile</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🏁</div>
              <div className="stat-content">
                <div className="stat-label">Best MPH</div>
                <div className="stat-value">{stats.bestMPH}</div>
                <div className="stat-subtext">Speed</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-label">Win Rate</div>
                <div className="stat-value">{stats.winRate}</div>
                <div className="stat-subtext">Success</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⏱️</div>
              <div className="stat-content">
                <div className="stat-label">Avg ET</div>
                <div className="stat-value">{stats.avgET}s</div>
                <div className="stat-subtext">Average</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⚡</div>
              <div className="stat-content">
                <div className="stat-label">Best RT</div>
                <div className="stat-value">{stats.bestRT}s</div>
                <div className="stat-subtext">Reaction</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <div className="stat-label">Avg RT</div>
                <div className="stat-value">{stats.avgRT}s</div>
                <div className="stat-subtext">Average RT</div>
              </div>
            </div>
          </div>

          {/* Detailed Metrics */}
          <div className="detailed-metrics">
            <h3 className="metrics-title">Detailed Performance</h3>
            <div className="metrics-grid">
              <div className="metric-item">
                <span className="metric-label">Best 60'</span>
                <span className="metric-value">{stats.best60}s</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Best 330'</span>
                <span className="metric-value">{stats.best330}s</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Best 1/4 ET</span>
                <span className="metric-value">{stats.best4ET}s</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Best 1/4 MPH</span>
                <span className="metric-value">{stats.best4MPH}</span>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          {Object.keys(stats.categories).length > 0 && (
            <div className="category-breakdown">
              <h3 className="metrics-title">Category Performance</h3>
              <div className="category-cards">
                {Object.entries(stats.categories).map(([category, data]) => (
                  <div key={category} className="category-card">
                    <div className="category-name">{category}</div>
                    <div className="category-stats">
                      <span className="cat-stat win-stat">{data.wins}W</span>
                      <span className="cat-stat">-</span>
                      <span className="cat-stat loss-stat">{data.losses}L</span>
                    </div>
                    <div className="category-rate">{Math.round((data.wins / data.total) * 100)}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts Section */}
          <div className="charts-section">
            <div className="charts-grid">
              {/* Win/Loss Pie Chart */}
              {chartData.winLoss.length > 0 && (
                <div className="chart-container">
                  <h3 className="chart-title">Win/Loss Ratio</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={chartData.winLoss}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.winLoss.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: 'rgba(0, 0, 0, 0.8)', border: 'none', borderRadius: '8px', color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Category Performance Bar Chart */}
              {chartData.categoryChart.length > 0 && (
                <div className="chart-container">
                  <h3 className="chart-title">Category Win Rate</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData.categoryChart} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="category" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} domain={[0, 100]} />
                      <Tooltip
                        contentStyle={{ background: 'rgba(0, 0, 0, 0.8)', border: 'none', borderRadius: '8px', color: '#fff' }}
                      />
                      <Bar dataKey="winRate" fill="#00d4ff" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ET Trend Line Chart */}
            {chartData.etTrend.length > 0 && (
              <div className="chart-container full-width">
                <h3 className="chart-title">ET Performance Trend</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData.etTrend} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="race" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'rgba(0, 0, 0, 0.8)', border: 'none', borderRadius: '8px', color: '#fff' }}
                      formatter={(value) => [value.toFixed(3), 'ET (seconds)']}
                    />
                    <Line
                      type="monotone"
                      dataKey="et"
                      stroke="#00d4ff"
                      dot={{ fill: '#00d4ff', r: 4 }}
                      activeDot={{ r: 6 }}
                      strokeWidth={2}
                      name="ET"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Performance Metrics */}
          <div className="performance-metrics">
            <div className="metric-row">
              <span className="metric-name">Consistency</span>
              <div className="metric-bar">
                <div className="metric-fill" style={{width: `${stats.consistency}%`}}></div>
              </div>
              <span className="metric-value">{stats.consistency}%</span>
            </div>
            <div className="metric-row">
              <span className="metric-name">Recent Form</span>
              <div className="metric-bar">
                <div className="metric-fill" style={{width: `${stats.recentForm}%`}}></div>
              </div>
              <span className="metric-value">{stats.recentForm}%</span>
            </div>
            <div className="metric-row">
              <span className="metric-name">Performance</span>
              <div className="metric-bar">
                <div className="metric-fill" style={{width: `${parseInt(stats.winRate)}%`}}></div>
              </div>
              <span className="metric-value">{stats.winRate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardModal;
